import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiEdit2, FiTrash2 } from "react-icons/fi";
import {
  deleteChildHealthProfile,
  fetchChildHealthProfile,
  saveChildHealthProfile,
  toUpdate,
} from "../healthProfileApi";

const inputClass =
  "w-full rounded-xl border border-emerald-200/70 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 disabled:opacity-60 dark:border-emerald-900/50 dark:bg-slate-900/60 dark:text-slate-100 dark:focus:border-emerald-600 dark:focus:ring-emerald-800/50";
const labelClass = "text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300";
const blockClass =
  "rounded-2xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-900/40";
const primaryButton =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-55";
const secondaryButton =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-emerald-700/40 bg-white px-4 text-sm font-bold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-55 dark:border-emerald-700/60 dark:bg-slate-800 dark:text-emerald-300";
const dangerButton =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-4 text-sm font-bold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-55 dark:border-rose-900/60 dark:bg-slate-800 dark:text-rose-300";

const text = (value) => (value === null || value === undefined ? "" : String(value));

const emptyDraft = {
  familyDoctorName: "",
  familyDoctorPhone: "",
  familyEmergencyContact: "",
  familyEmergencyPhone: "",
  ownDoctor: false,
  ownDoctorName: "",
  ownDoctorPhone: "",
  ownEmergencyContact: false,
  ownEmergencyName: "",
  ownEmergencyPhone: "",
  allergies: "",
  medicalNotes: "",
  swimmingDiploma: null,
};

const toDraft = (profile) =>
  !profile
    ? emptyDraft
    : {
        familyDoctorName: text(profile.family?.doctorName),
        familyDoctorPhone: text(profile.family?.doctorPhone),
        familyEmergencyContact: text(profile.family?.emergencyContact),
        familyEmergencyPhone: text(profile.family?.emergencyPhone),
        ownDoctor: !!profile.ownDoctor,
        ownDoctorName: text(profile.own?.doctorName),
        ownDoctorPhone: text(profile.own?.doctorPhone),
        ownEmergencyContact: !!profile.ownEmergencyContact,
        ownEmergencyName: text(profile.own?.emergencyContact),
        ownEmergencyPhone: text(profile.own?.emergencyPhone),
        allergies: text(profile.allergies),
        medicalNotes: text(profile.medicalNotes),
        swimmingDiploma:
          profile.swimmingDiploma === true || profile.swimmingDiploma === false ? profile.swimmingDiploma : null,
      };

const fromDraft = (draft) => ({
  family: {
    doctorName: draft.familyDoctorName,
    doctorPhone: draft.familyDoctorPhone,
    emergencyContact: draft.familyEmergencyContact,
    emergencyPhone: draft.familyEmergencyPhone,
  },
  ownDoctor: draft.ownDoctor,
  ownEmergencyContact: draft.ownEmergencyContact,
  own: {
    doctorName: draft.ownDoctorName,
    doctorPhone: draft.ownDoctorPhone,
    emergencyContact: draft.ownEmergencyName,
    emergencyPhone: draft.ownEmergencyPhone,
  },
  allergies: draft.allergies,
  medicalNotes: draft.medicalNotes,
  swimmingDiploma: draft.swimmingDiploma,
});

const Row = ({ label, value, t }) => (
  <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-3 py-1.5 text-sm">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className="min-w-0 whitespace-pre-line break-words font-semibold text-slate-800 dark:text-slate-100">
      {value || <span className="font-normal text-slate-400">{t("parentPortal.health.not_filled", { defaultValue: "Not filled in" })}</span>}
    </span>
  </div>
);

const Field = ({ label, value, onChange, disabled, multiline = false, type = "text", placeholder }) => (
  <label className="block space-y-1">
    <span className={labelClass}>{label}</span>
    {multiline ? (
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        disabled={disabled}
        placeholder={placeholder}
        className={`${inputClass} min-h-[88px] resize-y`}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className={inputClass}
      />
    )}
  </label>
);

/**
 * Health profile of a child in the parent portal: family doctor and emergency contact (shared by the children of
 * the family, a child can have its own) plus allergies, medical notes and swimming diploma per child. Only parents
 * see it; it fills in outing consent forms. Loads and saves itself.
 */
const ChildHealthProfilePanel = ({ t, childId, childName, startInEdit = false, onSaved }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteFamily, setDeleteFamily] = useState(false);
  const requestRef = useRef(0);
  const name = childName || t("common.unknown_child", { defaultValue: "your child" });

  const load = useCallback(async () => {
    if (!childId) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setLoadError("");
    try {
      const data = await fetchChildHealthProfile(childId);
      if (requestId !== requestRef.current) return;
      setProfile(data);
      setDraft(toDraft(data));
      setEditing(startInEdit && !data?.exists);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setLoadError(
        t("parentPortal.health.load_error", { defaultValue: "The contacts & notes could not be loaded." }),
      );
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [childId, startInEdit, t]);

  useEffect(() => {
    setEditing(false);
    setConfirmDelete(false);
    setNotice("");
    setError("");
    load();
  }, [load]);

  const update = (field) => (value) => setDraft((current) => ({ ...current, [field]: value }));

  const save = async (payload, successMessage) => {
    if (saving) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const data = await saveChildHealthProfile(childId, payload);
      setProfile(data);
      setDraft(toDraft(data));
      setEditing(false);
      setNotice(successMessage);
      onSaved?.(data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          t("parentPortal.health.save_error", { defaultValue: "Saving failed. Please try again." }),
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await deleteChildHealthProfile(childId, deleteFamily);
      setConfirmDelete(false);
      setDeleteFamily(false);
      await load();
      setNotice(t("parentPortal.health.deleted", { defaultValue: "The contacts & notes have been cleared." }));
    } catch (err) {
      setError(t("parentPortal.health.delete_error", { defaultValue: "Clearing failed. Please try again." }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-500">{t("common.loading", { defaultValue: "Loading..." })}</p>;
  }
  if (loadError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-rose-700 dark:text-rose-300">{loadError}</p>
        <button type="button" className={secondaryButton} onClick={load}>
          {t("common.retry", { defaultValue: "Try again" })}
        </button>
      </div>
    );
  }

  const hasFamilyData = Object.values(profile?.family || {}).some((value) => !!value);
  const hasAnyData = !!profile?.exists || hasFamilyData;

  const privacyNote = (
    <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400">
      {t("parentPortal.health.privacy_note", {
        defaultValue:
          "Only you and the other parent can see this. It is used to fill in consent forms for outings; the school only receives the details when you submit such a form, and they are removed after the outing.",
      })}
    </p>
  );

  if (editing) {
    return (
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save(fromDraft(draft), t("parentPortal.health.saved", { defaultValue: "The contacts & notes have been saved." }));
        }}
      >
        {privacyNote}

        <fieldset className={blockClass} disabled={saving}>
          <legend className="px-1 text-sm font-bold text-slate-900 dark:text-white">
            {t("parentPortal.health.family_title", { defaultValue: "For the whole family" })}
          </legend>
          <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
            {t("parentPortal.health.family_hint", {
              defaultValue: "Applies to all children in your family, unless you enter something different for a child.",
            })}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("parentPortal.trip_consent.doctor_name", { defaultValue: "Family doctor" })} value={draft.familyDoctorName} onChange={update("familyDoctorName")} />
            <Field type="tel" label={t("parentPortal.trip_consent.doctor_phone", { defaultValue: "Doctor's phone" })} value={draft.familyDoctorPhone} onChange={update("familyDoctorPhone")} />
            <Field label={t("parentPortal.trip_consent.emergency_contact", { defaultValue: "Emergency contact" })} value={draft.familyEmergencyContact} onChange={update("familyEmergencyContact")} />
            <Field type="tel" label={t("parentPortal.trip_consent.emergency_phone", { defaultValue: "Emergency phone" })} value={draft.familyEmergencyPhone} onChange={update("familyEmergencyPhone")} />
          </div>
        </fieldset>

        <fieldset className={blockClass} disabled={saving}>
          <legend className="px-1 text-sm font-bold text-slate-900 dark:text-white">
            {t("parentPortal.health.child_title", { defaultValue: "For {{childName}}", childName: name })}
          </legend>
          <div className="grid gap-3">
            <Field multiline label={t("parentPortal.trip_consent.allergies", { defaultValue: "Allergies" })} value={draft.allergies} onChange={update("allergies")} placeholder={t("parentPortal.trip_consent.allergies_hint", { defaultValue: "For example peanuts, pets, medication" })} />
            <Field multiline label={t("parentPortal.trip_consent.medical", { defaultValue: "Medical notes" })} value={draft.medicalNotes} onChange={update("medicalNotes")} placeholder={t("parentPortal.trip_consent.medical_hint", { defaultValue: "For example medication, permissions, special notes" })} />
            <label className="block space-y-1">
              <span className={labelClass}>{t("parentPortal.trip_consent.swimming", { defaultValue: "Swimming diploma" })}</span>
              <select
                className={inputClass}
                value={draft.swimmingDiploma === true ? "yes" : draft.swimmingDiploma === false ? "no" : "unknown"}
                onChange={(event) =>
                  update("swimmingDiploma")(event.target.value === "yes" ? true : event.target.value === "no" ? false : null)
                }
              >
                <option value="unknown">{t("parentPortal.trip_consent.swimming_unknown", { defaultValue: "Unknown" })}</option>
                <option value="yes">{t("common.yes", { defaultValue: "Yes" })}</option>
                <option value="no">{t("common.no", { defaultValue: "No" })}</option>
              </select>
            </label>

            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input type="checkbox" className="mt-1" checked={draft.ownDoctor} onChange={(event) => update("ownDoctor")(event.target.checked)} />
              <span>{t("parentPortal.health.own_doctor", { defaultValue: "{{childName}} has a different doctor", childName: name })}</span>
            </label>
            {draft.ownDoctor && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("parentPortal.trip_consent.doctor_name", { defaultValue: "Family doctor" })} value={draft.ownDoctorName} onChange={update("ownDoctorName")} />
                <Field type="tel" label={t("parentPortal.trip_consent.doctor_phone", { defaultValue: "Doctor's phone" })} value={draft.ownDoctorPhone} onChange={update("ownDoctorPhone")} />
              </div>
            )}

            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input type="checkbox" className="mt-1" checked={draft.ownEmergencyContact} onChange={(event) => update("ownEmergencyContact")(event.target.checked)} />
              <span>{t("parentPortal.health.own_emergency", { defaultValue: "{{childName}} has a different emergency contact", childName: name })}</span>
            </label>
            {draft.ownEmergencyContact && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("parentPortal.trip_consent.emergency_contact", { defaultValue: "Emergency contact" })} value={draft.ownEmergencyName} onChange={update("ownEmergencyName")} />
                <Field type="tel" label={t("parentPortal.trip_consent.emergency_phone", { defaultValue: "Emergency phone" })} value={draft.ownEmergencyPhone} onChange={update("ownEmergencyPhone")} />
              </div>
            )}
          </div>
        </fieldset>

        {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className={secondaryButton}
            disabled={saving}
            onClick={() => {
              setDraft(toDraft(profile));
              setEditing(false);
              setError("");
            }}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="submit" className={primaryButton} disabled={saving}>
            {saving ? "..." : t("common.actions.save", { defaultValue: "Save" })}
          </button>
        </div>
      </form>
    );
  }

  const effective = profile?.effective || {};
  const swimming =
    profile?.swimmingDiploma === true
      ? t("common.yes", { defaultValue: "Yes" })
      : profile?.swimmingDiploma === false
        ? t("common.no", { defaultValue: "No" })
        : "";

  return (
    <div className="space-y-4">
      {privacyNote}

      {profile?.reviewNeeded && (
        <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-start gap-2">
            <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {t("parentPortal.health.review_needed", {
              defaultValue: "New school year: please check whether these details are still correct.",
            })}
          </span>
          <button
            type="button"
            className={primaryButton}
            disabled={saving}
            onClick={() =>
              save(toUpdate(profile), t("parentPortal.health.confirmed", { defaultValue: "Thank you, the details are confirmed." }))
            }
          >
            {t("parentPortal.health.still_correct", { defaultValue: "Still correct" })}
          </button>
        </div>
      )}

      {notice && (
        <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
          <FiCheckCircle className="h-4 w-4" aria-hidden="true" />
          {notice}
        </p>
      )}

      {!hasAnyData ? (
        <div className={blockClass}>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t("parentPortal.health.empty", {
              defaultValue:
                "Nothing saved yet. Save the doctor, an emergency contact and allergies once, and outing forms are filled in for you.",
            })}
          </p>
        </div>
      ) : (
        <>
          <div className={blockClass}>
            <p className="mb-1 text-sm font-bold text-slate-900 dark:text-white">
              {t("parentPortal.health.contacts_title", { defaultValue: "Doctor and emergency contact" })}
            </p>
            <Row t={t} label={t("parentPortal.trip_consent.doctor_name", { defaultValue: "Family doctor" })} value={[effective.doctorName, effective.doctorPhone].filter(Boolean).join(" · ")} />
            <Row t={t} label={t("parentPortal.trip_consent.emergency_contact", { defaultValue: "Emergency contact" })} value={[effective.emergencyContact, effective.emergencyPhone].filter(Boolean).join(" · ")} />
            {!profile?.ownDoctor && !profile?.ownEmergencyContact && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t("parentPortal.health.shared_hint", { defaultValue: "Shared with the other children in your family." })}
              </p>
            )}
          </div>
          <div className={blockClass}>
            <p className="mb-1 text-sm font-bold text-slate-900 dark:text-white">
              {t("parentPortal.health.child_title", { defaultValue: "For {{childName}}", childName: name })}
            </p>
            <Row t={t} label={t("parentPortal.trip_consent.allergies", { defaultValue: "Allergies" })} value={profile?.allergies} />
            <Row t={t} label={t("parentPortal.trip_consent.medical", { defaultValue: "Medical notes" })} value={profile?.medicalNotes} />
            <Row t={t} label={t("parentPortal.trip_consent.swimming", { defaultValue: "Swimming diploma" })} value={swimming} />
          </div>
        </>
      )}

      {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}

      {confirmDelete ? (
        <div className="space-y-3 rounded-2xl border border-rose-200 bg-rose-50/70 p-4 dark:border-rose-900/60 dark:bg-rose-950/20">
          <p className="text-sm font-semibold text-rose-800 dark:text-rose-200">
            {t("parentPortal.health.delete_confirm", {
              defaultValue: "Clear the contacts & notes of {{childName}}?",
              childName: name,
            })}
          </p>
          {hasFamilyData && (
            <label className="flex items-start gap-2 text-sm text-rose-800 dark:text-rose-200">
              <input type="checkbox" className="mt-1" checked={deleteFamily} onChange={(event) => setDeleteFamily(event.target.checked)} />
              <span>
                {t("parentPortal.health.delete_family", {
                  defaultValue: "Also clear the family doctor and emergency contact (for all children in the family)",
                })}
              </span>
            </label>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={secondaryButton} disabled={saving} onClick={() => setConfirmDelete(false)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="button" className={dangerButton} disabled={saving} onClick={remove}>
              <FiTrash2 className="h-4 w-4" aria-hidden="true" />
              {t("parentPortal.health.delete", { defaultValue: "Clear" })}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2">
          {hasAnyData && (
            <button type="button" className={dangerButton} onClick={() => setConfirmDelete(true)}>
              <FiTrash2 className="h-4 w-4" aria-hidden="true" />
              {t("parentPortal.health.delete", { defaultValue: "Clear" })}
            </button>
          )}
          <button
            type="button"
            className={primaryButton}
            onClick={() => {
              setDraft(toDraft(profile));
              setNotice("");
              setEditing(true);
            }}
          >
            <FiEdit2 className="h-4 w-4" aria-hidden="true" />
            {hasAnyData
              ? t("common.actions.edit", { defaultValue: "Edit" })
              : t("parentPortal.health.fill_in", { defaultValue: "Fill in" })}
          </button>
        </div>
      )}
    </div>
  );
};

export default ChildHealthProfilePanel;
